import React, { useState } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Modal,
  Text,
  Textarea,
} from '@workwhile/ui'
import { useDayforceJobContextValue } from 'pages/Shifts/ShiftDetail/context/DayforceJobProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { DAYFORCE_SHIFTS_DEFAULT_TIMEZONE } from 'pages/Dayforce/constants'
import { z } from 'zod'

export const createContestedHoursRequestSchema = z.object({
  associationId: z.number(),
  requestedStartedAt: z.coerce.date(),
  requestedFinishedAt: z.coerce.date(),
  requestedBreakDuration: z.coerce.number().nullable(),
  requestedReason: z.string(),
})

export type CreateContestedHoursRequest = z.infer<
  typeof createContestedHoursRequestSchema
>

const parseTimeRangeValueToUTCDate = (
  time: string | Date,
  timezone: string
): Date => {
  // Standardizes TimeRangeInputValue to a Date object in UTC
  if (time instanceof Date) {
    return fromZonedTime(time, timezone)
  }

  return fromZonedTime(new Date(time), timezone)
}

export const CreateContestedHoursRequestButton = ({
  associationId,
  workerName,
  shiftStartsAt,
  shiftEndsAt,
  startedAt,
  finishedAt,
  initialRequestedStartedAt,
  initialRequestedFinishedAt,
  initialRequestedBreakDuration,
}: {
  associationId: number
  workerName: string
  shiftStartsAt: string
  shiftEndsAt: string
  startedAt?: string | null
  finishedAt?: string | null
  initialRequestedStartedAt?: string
  initialRequestedFinishedAt?: string
  initialRequestedBreakDuration?: number | null
}) => {
  const {
    actions: { createContestedHoursRequest },
    state: { contestedHoursRequestMutationState },
  } = useDayforceJobContextValue()

  const [isOpen, setIsOpen] = useState(false)

  const zonedRequestedStartedAt = initialRequestedStartedAt
    ? formatInTimeZone(
        initialRequestedStartedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        "yyyy-MM-dd'T'HH:mm"
      )
    : undefined
  const zonedRequestedFinishedAt = initialRequestedFinishedAt
    ? formatInTimeZone(
        initialRequestedFinishedAt,
        DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
        "yyyy-MM-dd'T'HH:mm"
      )
    : undefined

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<CreateContestedHoursRequest>({
    resolver: zodResolver(createContestedHoursRequestSchema),
    defaultValues: {
      associationId,
      requestedStartedAt: zonedRequestedStartedAt
        ? new Date(zonedRequestedStartedAt)
        : undefined,
      requestedFinishedAt: zonedRequestedFinishedAt
        ? new Date(zonedRequestedFinishedAt)
        : undefined,
      requestedBreakDuration: initialRequestedBreakDuration,
      requestedReason: '',
    },
  })

  const handleClick = () => {
    setIsOpen(true)
  }

  const onSubmit = handleSubmit((data) => {
    createContestedHoursRequest(
      {
        associationId,
        requestedStartedAt: data.requestedStartedAt.toISOString(),
        requestedFinishedAt: data.requestedFinishedAt.toISOString(),
        requestedBreakDuration: data.requestedBreakDuration,
        requestedReason: data.requestedReason,
      },
      {
        onSuccess: () => {
          setIsOpen(false)
        },
      }
    )
  })

  const handleCancel = () => {
    setIsOpen(false)
  }

  return (
    <>
      <Button
        message="Are you sure you want to contest this worker's hours? This request will be in a pending state in Dayforce until it is approved."
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={contestedHoursRequestMutationState.isPending}
        loading={contestedHoursRequestMutationState.isPending}
      >
        <Text fontSize="12px">Contest hours</Text>
      </Button>
      {isOpen ? (
        <Modal
          open
          onClose={handleCancel}
          title={`Contesting hours for ${workerName}`}
        >
          <Text mb={2}>
            Contesting hours will be in a pending state in Dayforce until it is
            approved.
          </Text>
          <Flex mb={4} alignItems="center">
            <Box width="50%">
              <Text fontSize={2}>Shift Start</Text>
              <Text fontSize={2} color="lightText">
                {formatInTimeZone(
                  shiftStartsAt,
                  DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
                  'yyyy-MM-dd, hh:mm a zzz'
                )}
              </Text>
            </Box>
            <Box width="50%">
              <Text fontSize={2}>Shift End</Text>
              <Text fontSize={2} color="lightText">
                {formatInTimeZone(
                  shiftEndsAt,
                  DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
                  'yyyy-MM-dd, hh:mm a zzz'
                )}
              </Text>
            </Box>
          </Flex>
          <Flex mb={4} alignItems="center">
            <Box width="50%">
              <Text fontSize={2}>Clock-in WorkWhile</Text>
              <Text fontSize={2} color="lightText">
                {startedAt
                  ? formatInTimeZone(
                      startedAt,
                      DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
                      'yyyy-MM-dd, hh:mm a zzz'
                    )
                  : '-'}
              </Text>
            </Box>
            <Box width="50%">
              <Text fontSize={2}>Clock-out WorkWhile</Text>
              <Text fontSize={2} color="lightText">
                {finishedAt
                  ? formatInTimeZone(
                      finishedAt,
                      DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
                      'yyyy-MM-dd, hh:mm a zzz'
                    )
                  : '-'}
              </Text>
            </Box>
          </Flex>
          <form onSubmit={onSubmit}>
            <Controller
              name="requestedStartedAt"
              control={control}
              render={({ field }) => {
                const zonedRequestedStartedAt = field?.value
                  ? formatInTimeZone(
                      field.value,
                      DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
                      "yyyy-MM-dd'T'HH:mm"
                    )
                  : undefined

                return (
                  <Field
                    label="Requested started at"
                    error={errors.requestedStartedAt?.message}
                  >
                    <Input
                      type="datetime-local"
                      step={60}
                      value={zonedRequestedStartedAt}
                      onChange={(e) => {
                        if (!e.target.value) {
                          return
                        }

                        const parsedDate = parseTimeRangeValueToUTCDate(
                          e.target.value,
                          DAYFORCE_SHIFTS_DEFAULT_TIMEZONE
                        )

                        setValue('requestedStartedAt', parsedDate)
                      }}
                    />
                  </Field>
                )
              }}
            />
            <Controller
              name="requestedFinishedAt"
              control={control}
              render={({ field }) => {
                const zonedRequestedFinishedAt = field?.value
                  ? formatInTimeZone(
                      field.value,
                      DAYFORCE_SHIFTS_DEFAULT_TIMEZONE,
                      "yyyy-MM-dd'T'HH:mm"
                    )
                  : undefined

                return (
                  <Field
                    label="Requested finished at"
                    error={errors.requestedFinishedAt?.message}
                  >
                    <Input
                      type="datetime-local"
                      step={60}
                      value={zonedRequestedFinishedAt}
                      onChange={(e) => {
                        if (!e.target.value) {
                          return
                        }

                        const parsedDate = parseTimeRangeValueToUTCDate(
                          e.target.value,
                          DAYFORCE_SHIFTS_DEFAULT_TIMEZONE
                        )

                        setValue('requestedFinishedAt', parsedDate)
                      }}
                    />
                  </Field>
                )
              }}
            />
            <Field
              label="Requested break duration (hours)"
              error={errors.requestedBreakDuration?.message}
            >
              <Input type="number" {...register('requestedBreakDuration')} />
            </Field>
            <Field label="Reason" error={errors.requestedReason?.message}>
              <Textarea {...register('requestedReason')} />
            </Field>

            <Flex flexDirection="row" justifyContent="space-between" gap={2}>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={contestedHoursRequestMutationState.isPending}
                disabled={contestedHoursRequestMutationState.isPending}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
